import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSharesSummary,
  getPersonalShareTranscations,
  exportMemberTransCsv,
  exportMemberTransPdf,
} from "../../sdk/shares/shares";
import { getProductPenalties } from "../../sdk/savings/savings";
import {
  sharesManualTransaction,
  uploadFile,
} from "../../sdk/customers/customers";
import { downloadCSV, downloadPDF } from "../../hooks/download";
import ExportPdfCsv from "../../components/export/ExportPdfCsv";
import { capitalize } from "../../helpers/capitalize";
import TransactionModal from "../../components/Modals/transactions/TransactionModal";
import ViewReceipt from "../../components/Modals/transactions/viewReceipt";
import LoadingButtons from "../../components/Modals/edit-customer/LoadingButtons";
import SharesAddTransaction from "../../components/Modals/transactions/sharesAddTransactions";
import PenaltyLedger from "../../components/Modals/transactions/penaltyLedger";
import PaymentAddedSuccessfully from "../../components/Modals/transactions/paymentAddedSuccessfully";
import Pagination from "../../shared/Pagination";
import { toast } from "react-toastify";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";

const ShareView = () => {
  const [sharesSummary, setSharesSummary] = useState(null);
  const [penaltyLedger, setPenaltyLedger] = useState([]);

  const [personalSharesTransactions, setPersonalSharesTransactions] =
    useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const editPopupRef = useRef(null);

  const navigate = useNavigate();
  const { memberId } = useParams();

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (memberId) {
      fetchSharesSummary(memberId);
    }
  }, [memberId]);

  const fetchSharesSummary = async (memberId) => {
    try {
      const res = await getSharesSummary(memberId);
      setSharesSummary(res?.data);
    } catch (error) {
      console.error("Error fetching summary data:", error);
    }
  };

  let [currentPage, setCurrentPage] = useState(1);
  let [totalPages, setTotalPages] = useState(null);
  const [limit, setLimit] = useState(10);
  const [displayedPage, setDisplayedPage] = useState(1);
  const [totalNoOfLoans, setTotalNoOfLoans] = useState(null);

  const [penaltyCurrentPage, setPenaltyCurrentPage] = useState(1);
  const [penaltyLimit, setPenaltyLimit] = useState(50);
  const [penaltyDisplayedPage, setPenaltyDisplayedPage] = useState(1);
  const [penaltyTotalPages, setPenaltyTotalPages] = useState(1);
  const [penaltyTotalRecords, setPenaltyTotalRecords] = useState(0);

  const fetchPersonalShareTranscations = async () => {
    try {
      const response = await getPersonalShareTranscations(
        currentPage,
        limit,
        memberId,
      );

      setPersonalSharesTransactions(response?.data?.items);

      setDisplayedPage(Number(response?.data?.meta?.currentPage));

      setTotalPages(Math.ceil(Number(response?.data?.meta?.totalPages)));
      setTotalNoOfLoans(response?.data?.meta?.totalItems);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    fetchPersonalShareTranscations();
  }, [currentPage, limit, memberId]);

  const toggleEditPopup = () => {
    setShowEditPopup(!showEditPopup);
  };

  const handleClickOutside = (event) => {
    if (editPopupRef.current && !editPopupRef.current.contains(event.target)) {
      setShowEditPopup(false);
    }
  };

  useEffect(() => {
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setCurrentPage(1);
  };

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 3); i++) {
      pages.push(
        <div
          key={i}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === i
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(i)}
        >
          <p>{i}</p>
        </div>,
      );
    }

    return pages;
  };

  const handleExportAllTransactions = async (exportType, memberId) => {
    let response;

    if (exportType === "pdf") {
      response = await exportMemberTransPdf(memberId);
      await downloadPDF(response, "Shares_transactions.pdf");
    } else if (exportType === "csv") {
      response = await exportMemberTransCsv(memberId);
      await downloadCSV(response, "shares_transactions.csv");
    }
  };

  const [singleTransaction, setSingleTransaction] = useState({});
  const [openTransaction, setOpenTransaction] = useState(false);
  const openTransactionModal = (transaction) => {
    setSingleTransaction(transaction);
    setOpenTransaction(true);
  };
  const closeTransactionModal = () => {
    setOpenTransaction(false);
  };

  const [viewReceiptOpen, setViewReceiptOpen] = useState(false);

  const openViewReceipt = () => {
    setViewReceiptOpen(true);
    closeTransactionModal();
  };

  const closeViewReceipt = () => {
    setViewReceiptOpen(false);
  };

  const [sharesTransMode, setSharesTransMode] = useState("MPESA");
  const [sharesAmount, setSharesAmount] = useState("");
  const [sharesDate, setSharesDate] = useState(null);
  const [sharesAccNumber, setSharesAccNumber] = useState("");
  const [sharesRefNumber, setSharesRefNumber] = useState("");
  const [sharesDocFile, setSharesDocFile] = useState(null);
  const [isChecked, setIsChecked] = useState(false);

  const [isLoadingButtons, setIsLoadingButtons] = useState(false);

  const [isSharesAddTransactionOpen, setIsSharesAddTransactionOpen] =
    useState(false);

  const openSharesAddTransactionOpen = () => {
    setIsSharesAddTransactionOpen(true);
  };

  const closeSharesAddTransactionOpen = () => {
    setIsSharesAddTransactionOpen(false);
  };

  const [isPaymentAddedSuccessfullyOpen, setIsPaymentAddedSuccessfullyOpen] =
    useState(false);

  const closePaymentAddedSuccessfully = () => {
    setIsPaymentAddedSuccessfullyOpen(false);
  };

  const handleSharesManualTransaction = async () => {
    setIsSharesAddTransactionOpen(false);
    setIsLoadingButtons(true);

    try {
      let documentUrl = null;

      // Upload the document file first (if present)
      if (sharesDocFile) {
        try {
          documentUrl = await uploadFile(sharesDocFile);
        } catch (uploadError) {
          setIsLoadingButtons(false);
          toast?.error(
            uploadError?.message || "Error uploading document",
            uploadError,
          );
          return;
        }
      }

      // Now proceed with the manual transaction using the uploaded file URL
      await sharesManualTransaction({
        deposit_method: sharesTransMode,
        type: "credit",
        amount: sharesAmount,
        customer_id: sharesSummary?.customerId,
        account_id: sharesSummary?.accountId,
        currency_id: "KES",
        ref_number: sharesRefNumber,
        withdrawal_method: "none",
        phone_number: sharesAccNumber,
        document_url: documentUrl,
        transaction_date: sharesDate,
      });

      setIsLoadingButtons(false);
      fetchPersonalShareTranscations();
      setIsPaymentAddedSuccessfullyOpen(true);

      // Reset form
      setSharesAmount("");
      setSharesRefNumber("");
      setSharesAccNumber("");
      setSharesDate("");
      setSharesDocFile(null);
      setIsChecked(false);

      setTimeout(() => {
        setIsPaymentAddedSuccessfullyOpen(false);
      }, 1000);
    } catch (error) {
      setIsLoadingButtons(false);
      toast?.error(error?.message || "Error adding manual transaction", error);
    }
  };

  const [isPenaltyLedger, setIsPenaltyLedger] = useState(false);

  const openPenaltyLedger = () => {
    setIsPenaltyLedger(true);
  };

  const closePenaltyLedger = () => {
    setIsPenaltyLedger(false);
  };

  const fetchPenaltyLedger = async () => {
    try {
      const productId = "94e9dd35-b90f-b3d8-dcc1-2f7ae4fc3d6b";

      const response = await getProductPenalties(
        penaltyCurrentPage,
        penaltyLimit,
        memberId,
        productId,
      );

      setPenaltyLedger(response?.items || []);
      setPenaltyDisplayedPage(Number(response?.meta?.currentPage || 1));

      setPenaltyTotalPages(Number(response?.meta?.totalPages || 1));

      setPenaltyTotalRecords(Number(response?.meta?.totalItems || 0));
    } catch (error) {
      console.log("Failed to fetch penalty ledger");
    }
  };
  useEffect(() => {
    fetchPersonalShareTranscations();
    fetchPenaltyLedger();
  }, [memberId, penaltyCurrentPage, penaltyLimit]);

  return (
    <div>
      <div className="h-[118px] bg-[#074073] w-full flex items-center justify-between px-[20px]">
        <div className="flex items-center gap-[30px]">
          <div className="h-[80px] w-[80px] text-[30px] text-[#074073] rounded-[100%] bg-[#D3EAFF] flex items-center justify-center">
            {`${sharesSummary?.memberName?.split(" ")[0]?.charAt(0) || ""}${
              sharesSummary?.memberName
                ?.split(" ")
                [sharesSummary?.memberName?.split(" ").length - 1]?.charAt(0) ||
              "No Initials"
            }`.toUpperCase()}
          </div>
          <div className="flex flex-col">
            <p className="text-[#D3EAFF] text-[14px]">
              #{memberId || "No ID Available"}
            </p>
            <p className="text-white text-[24px]">
              {sharesSummary?.memberName
                ?.split(" ")
                .map(
                  (name) =>
                    name?.charAt(0)?.toUpperCase() +
                    name?.slice(1)?.toLowerCase(),
                )
                .join(" ") || "No Member Name"}
            </p>
          </div>
        </div>
      </div>
      <div className="my-[10px] px-[20px]">
        <button
          onClick={handleBack}
          className="bg-[#074073] h-[40px] text-white text-[16px] w-[140px] rounded flex items-center justify-center gap-[10px] px-[20px]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.78em"
            height="1em"
            viewBox="0 0 16 9"
          >
            <path
              fill="currentColor"
              d="M12.5 5h-9c-.28 0-.5-.22-.5-.5s.22-.5.5-.5h9c.28 0 .5.22.5.5s-.22.5-.5.5"
            />
            <path
              fill="currentColor"
              d="M6 8.5a.47.47 0 0 1-.35-.15l-3.5-3.5c-.2-.2-.2-.51 0-.71L5.65.65c.2-.2.51-.2.71 0c.2.2.2.51 0 .71L3.21 4.51l3.15 3.15c.2.2.2.51 0 .71c-.1.1-.23.15-.35.15Z"
            />
          </svg>
          Back
        </button>
      </div>
      <div className="p-[10px] h-[700px] overflow-y-auto">
        <div
          className="w-full mb-[10px] px-[12px] py-[5px] bg-white rounded-[10px]"
          style={{ boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.25)" }}
        >
          <p className="text-[#074073] text-[16px]">Account</p>
          <div className="w-full flex justify-between mt-2">
            <div className="w-[85%] flex gap-[20px]">
              <div className="bg-image-1 h-[165px] bg-[#007F7B] !bg-[#007F7B] mb-[20px] rounded-[10px] w-[500px] p-[10px]">
                <div className="w-full flex items-center justify-between">
                  <p className="text-[#FFFFFFB2] text-[12px]">Account Type</p>
                  <p className="text-[#FFFFFFB2] text-[12px]">Account Number</p>
                </div>
                <div className="w-full flex items-center justify-between">
                  <p className="text-[#FFFFFF] text-[20px]">
                    {sharesSummary?.accountType}
                  </p>
                  <p className="text-[#FFFFFF] text-[20px]">
                    {sharesSummary?.accountNumber || "No Account Number"}
                  </p>
                </div>
                <div>
                  <p className="text-[#FFFFFFB2] text-[12px]">
                    Account Balance
                  </p>
                </div>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-[5px]">
                    <p className="text-white font-bold">KES.</p>
                    <p
                      className=""
                      style={{ color: "white", fontSize: "16px" }}
                    >
                      <span className="text-[36px] text-white font-bold">
                        {Number(
                          sharesSummary?.availableBalance || "0.00",
                        )?.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-8 items-start">
                {/* Left column */}
                <div className="flex flex-col gap-7 w-[20vw]">
                  <div className="flex items-center justify-between">
                    <p>Created On</p>
                    <p className="text-right">
                      {sharesSummary?.memberCreatedAt
                        ? new Date(sharesSummary?.memberCreatedAt)
                            ?.toISOString()
                            ?.split("T")[0]
                        : "No Member Data"}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <p>Investment Purchase Status</p>
                    <p className="text-right text-[#0C722F]">
                      {sharesSummary?.status
                        ?.split("   ")
                        ?.map(
                          (name) =>
                            name?.charAt(0)?.toUpperCase() +
                            name?.slice(1)?.toLowerCase(),
                        )
                        ?.join(" ")}
                    </p>
                  </div>

                  {Number(sharesSummary?.outstandingPenalties) > 0 && (
                    <div className="flex items-center justify-between">
                      <p>Outstanding Penalties</p>
                      <p className="text-right">
                        KES{" "}
                        {Number(
                          sharesSummary?.outstandingPenalties || "0.00",
                        )?.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  )}
                </div>

                {/* Right column */}
                {Number(sharesSummary?.outstandingPenalties) > 0 && (
                  <div className="flex flex-col gap-7">
                    <div className="h-[20px]" />
                    <div className="h-[20px]" />

                    <button
                      type="button"
                      onClick={openPenaltyLedger}
                      className="text-[#3B82F6] underline cursor-pointer whitespace-nowrap self-start"
                    >
                      View Penalty Ledger
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div
              className="relative flex gap-2 w-[94] px-2 items-center justify-center cursor-pointer bg-[#DBF227] rounded-[5px]"
              style={{ height: "30px" }}
              onClick={toggleEditPopup}
            >
              <svg
                width="10"
                height="12"
                viewBox="0 0 10 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.61581 0.681373C5.93181 0.302373 6.54731 0.561373 6.49731 1.05237L6.14431 4.50137H8.99981C9.09482 4.50141 9.18785 4.52851 9.26801 4.57951C9.34817 4.63051 9.41214 4.70329 9.45243 4.78933C9.49273 4.87537 9.50767 4.9711 9.49552 5.06533C9.48337 5.15956 9.44462 5.24837 9.38381 5.32137L4.38381 11.3214C4.06781 11.7004 3.45231 11.4414 3.50231 10.9504L3.85531 7.50137H0.999813C0.904806 7.50134 0.811775 7.47423 0.731615 7.42324C0.651455 7.37224 0.587483 7.29946 0.547191 7.21342C0.506899 7.12738 0.491955 7.03164 0.504108 6.93741C0.516261 6.84319 0.555008 6.75437 0.615813 6.68137L5.61581 0.681373Z"
                  fill="#074073"
                />
              </svg>
              <p className="text-#074073 text-[12px] font-normal">Actions</p>
              <svg
                width="11"
                height="4"
                viewBox="0 0 11 4"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.5 4L0.736861 0.25L10.2631 0.250001L5.5 4Z"
                  fill="#074073"
                />
              </svg>
              {showEditPopup && (
                <div
                  ref={editPopupRef}
                  className="absolute right-0 top-full mt-2 w-[180px]  bg-white shadow-md border border-[#E0E0E0] rounded-md z-50"
                >
                  <div className="cursor-pointer hover:bg-gray-100 rounded px-2 py-2">
                    <p className="text-[12px] text-[#000000] font-[300]">
                      Extend purchase period
                    </p>
                  </div>
                  {/* <div className="cursor-pointer hover:bg-gray-100 rounded px-2 py-2">
                    <p className="text-[12px] text-[#000000] font-[300]">
                      List Shares for Sale
                    </p>
                  </div> */}
                </div>
              )}
            </div>
          </div>

          <PermissionGate required={[PERMISSIONS?.SH?.VIEW_HISTORY]}>
            <div className="flex items-center justify-between w-[100%] mx-auto">
              <div className="relative items-left justify-center">
                <p className="text-[#074073] text-[16px] font-[500]">
                  Shares Purchase History
                </p>
              </div>

              <div className="flex items-center gap-3">
                <PermissionGate required={[PERMISSIONS?.SH?.ADD_DEPOSIT]}>
                  <button
                    onClick={openSharesAddTransactionOpen}
                    className="block text-white bg-[#074073] focus:outline-none focus:ring-[#074073] rounded-[30px] text-center px-5 py-2.5"
                    type="button"
                  >
                    <div className="flex items-center justify-center gap-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M11 13H5V11H11V5H13V11H19V13H13V19H11V13Z"
                          fill="white"
                        />
                      </svg>
                      <p className="text-[#FFF] text-[14px]">Add Payment</p>
                    </div>
                  </button>
                </PermissionGate>
                <PermissionGate required={[PERMISSIONS?.SH?.EXPORT]}>
                  <ExportPdfCsv
                    handleExportAllCustomers={handleExportAllTransactions}
                    memberId={memberId}
                  />
                </PermissionGate>
              </div>
            </div>
            <div className="flex flex-col w-full mt-3">
              <div className="w-full flex items-center bg-[#E3F8FF] px-[10px] min-h-[44px]">
                <div className="w-[10%]">
                  <input type="checkbox" name="" id="" />
                </div>
                <p className="w-[20%] text-[#000]">TRANSACTION ID</p>
                <p className="w-[20%] text-[#000]">AMOUNT</p>
                <p className="w-[20%] text-[#000]">TYPE</p>
                <p className="w-[20%] text-[#000]">BALANCE</p>
                <p className="w-[20%] text-[#000]">DATE OF TRANSACTION</p>
                <p className="w-[14%] text-[#000]">STATUS</p>
              </div>
              <div className="h-[430px] overflow-y-auto">
                {personalSharesTransactions?.length > 0 ? (
                  <>
                    {personalSharesTransactions?.map((transaction) => (
                      <div
                        key={transaction?.transactionId}
                        className="w-full flex cursor-pointer items-center bg-white px-[10px] min-h-[44px]"
                      >
                        <div className="w-[10%]">
                          <input
                            type="checkbox"
                            name="transaction"
                            id="transaction"
                          />
                        </div>

                        <PermissionGate
                          required={[PERMISSIONS?.SV?.VIEW_TRANSACTION_DETAILS]}
                        >
                          <p
                            onClick={() => openTransactionModal(transaction)}
                            className="w-[20%] text-[#037BD1]"
                          >
                            {transaction?.transactionId}
                          </p>
                        </PermissionGate>
                        <PermissionGate
                          required={[PERMISSIONS?.SV?.VIEW_TRANSACTION_DETAILS]}
                          fallback={
                            <p className="w-[20%] text-[#037BD1] cursor-not-allowed">
                              {transaction?.transactionId}
                            </p>
                          }
                        />
                        <p className="w-[20%]">
                          KES &nbsp;
                          {Number(
                            transaction?.amount || "0.00",
                          )?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>

                        <p className="w-[20%]">{transaction?.type}</p>
                        <p className="w-[20%]">
                          KES &nbsp;
                          {Number(
                            transaction?.balance || "0.00",
                          )?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                        <p className="w-[20%]">
                          {transaction?.date
                            ? new Date(transaction?.date)
                                ?.toISOString()
                                ?.split("T")[0]
                            : "N/A"}
                        </p>

                        <div className="w-[14%] flex items-center gap-[10px]">
                          {transaction?.status === "completed" && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 0C8.73438 0 9.44271 0.09375 10.125 0.28125C10.8073 0.46875 11.4427 0.739583 12.0312 1.09375C12.6198 1.44792 13.1589 1.86458 13.6484 2.34375C14.138 2.82292 14.5573 3.36198 14.9062 3.96094C15.2552 4.5599 15.5234 5.19792 15.7109 5.875C15.8984 6.55208 15.9948 7.26042 16 8C16 8.73438 15.9062 9.44271 15.7188 10.125C15.5312 10.8073 15.2604 11.4427 14.9062 12.0312C14.5521 12.6198 14.1354 13.1589 13.6562 13.6484C13.1771 14.138 12.638 14.5573 12.0391 14.9062C11.4401 15.2552 10.8021 15.5234 10.125 15.7109C9.44792 15.8984 8.73958 15.9948 8 16C7.26562 16 6.55729 15.9062 5.875 15.7188C5.19271 15.5312 4.55729 15.2604 3.96875 14.9062C3.38021 14.5521 2.84115 14.1354 2.35156 13.6562C1.86198 13.1771 1.44271 12.638 1.09375 12.0391C0.744792 11.4401 0.476562 10.8021 0.289062 10.125C0.101562 9.44792 0.00520833 8.73958 0 8C0 7.26562 0.09375 6.55729 0.28125 5.875C0.46875 5.19271 0.739583 4.55729 1.09375 3.96875C1.44792 3.38021 1.86458 2.84115 2.34375 2.35156C2.82292 1.86198 3.36198 1.44271 3.96094 1.09375C4.5599 0.744792 5.19792 0.476562 5.875 0.289062C6.55208 0.101562 7.26042 0.00520833 8 0ZM12.7109 5.35156L11.6484 4.28906L6.5 9.4375L4.35156 7.28906L3.28906 8.35156L6.5 11.5625L12.7109 5.35156Z"
                                fill="#0C722F"
                              />
                            </svg>
                          )}
                          {transaction?.status === "pending" && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                                fill="#554D4D"
                              />
                            </svg>
                          )}
                          <p className="text-[#0C722F]">
                            {" "}
                            {capitalize(transaction?.status)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="mt-[10px]">No record of transaction</p>
                )}
                <div className="bg-white w-[98%] px-[10px] mx-auto mb-[20px] flex items-center justify-between mt-5">
                  <div className="flex items-center gap-[20px]">
                    <p className="text-[#074073] text-[14px]">Rows per page</p>
                    <select
                      name="page"
                      id="page"
                      className="w-[71px] border-[1px] px-[5px] h-[30px]"
                      value={limit}
                      onChange={handleLimitChange}
                    >
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                      <option value="25">25</option>
                    </select>
                    <p className="text-[#074073] text-[14px]">
                      {displayedPage} / {totalPages} ({totalNoOfLoans} records)
                    </p>
                  </div>
                  <div className="flex items-center gap-[15px]">
                    <p
                      onClick={() =>
                        handlePageChange(Math.max(currentPage - 1, 1))
                      }
                      className="text-[14px] text-[#074073] cursor-pointer"
                    >
                      Prev
                    </p>
                    {renderPages()}
                    <div
                      className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                        Number(currentPage) > 3
                          ? "bg-[#074073] text-white"
                          : "bg-[#F5F5F5] text-[#074073]"
                      }`}
                    >
                      <p>{Number(currentPage) > 3 ? currentPage : "..."}</p>
                    </div>
                    <p
                      onClick={() =>
                        handlePageChange(Math.min(currentPage + 1, totalPages))
                      }
                      className="text-[14px] cursor-pointer text-[#074073]"
                    >
                      Next
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </PermissionGate>
        </div>
      </div>

      <TransactionModal
        transactionDetailsOpen={openTransaction}
        closeTransactionDetails={closeTransactionModal}
        singleTransaction={singleTransaction}
        openViewReceipt={openViewReceipt}
      />

      <ViewReceipt
        viewReceiptOpen={viewReceiptOpen}
        closeViewReceipt={closeViewReceipt}
        singleTransaction={singleTransaction}
      />

      <LoadingButtons isLoadingButtons={isLoadingButtons} />

      <PaymentAddedSuccessfully
        id="id_R"
        isPaymentAddedSuccessfullyOpen={isPaymentAddedSuccessfullyOpen}
        closePaymentAddedSuccessfully={closePaymentAddedSuccessfully}
      />

      <SharesAddTransaction
        id="id_R"
        isSharesAddTransactionOpen={isSharesAddTransactionOpen}
        closeSharesAddTransactionOpen={closeSharesAddTransactionOpen}
        sharesTransMode={sharesTransMode}
        setSharesTransMode={setSharesTransMode}
        sharesAmount={sharesAmount}
        setSharesAmount={setSharesAmount}
        sharesDate={sharesDate}
        setSharesDate={setSharesDate}
        sharesAccNumber={sharesAccNumber}
        setSharesAccNumber={setSharesAccNumber}
        sharesRefNumber={sharesRefNumber}
        setSharesRefNumber={setSharesRefNumber}
        sharesDocFile={sharesDocFile}
        setSharesDocFile={setSharesDocFile}
        handleSharesManualTransaction={handleSharesManualTransaction}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
      />

      <PenaltyLedger
        isPenaltyLedger={isPenaltyLedger}
        handleClosePenaltyLedger={closePenaltyLedger}
        accountNumber={sharesSummary?.accountNumber}
        ledger={penaltyLedger}
        account={sharesSummary?.accountType}
        penaltyLimit={penaltyLimit}
        setPenaltyLimit={setPenaltyLimit}
        Pagination={Pagination}
        penaltyCurrentPage={penaltyCurrentPage}
        setPenaltyCurrentPage={setPenaltyCurrentPage}
        penaltyDisplayedPage={penaltyDisplayedPage}
        setPenaltyDisplayedPage={setPenaltyDisplayedPage}
        penaltyTotalPages={penaltyTotalPages}
        setPenaltyTotalPages={setPenaltyTotalPages}
        penaltyTotalRecords={penaltyTotalRecords}
        setPenaltyTotalRecords={setPenaltyTotalRecords}
        // handleExport={handleExport}
      />
    </div>
  );
};

export default ShareView;
